import { Text } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Stack,
  TextField,
  ThemeProvider,
} from "@mui/material";
import { RootPaths } from "@src/appV2/App/paths";
import { ClipboardLogo } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { isCapacitorPlatform } from "@src/appV2/lib/utils/capacitor/platformUtils";
import { AnimatedBackgroundPageWrapper } from "@src/appV2/redesign/components/AnimatedBackgroundPageWrapper";
// eslint-disable-next-line no-restricted-imports
import { getCognitoSingleton } from "@src/lib/cognito/src/lib/cognitoSingleton";
// eslint-disable-next-line no-restricted-imports
import { getMagicLinkEmail } from "@src/lib/cognito/src/lib/magicLinkEmailStore";
// eslint-disable-next-line no-restricted-imports
import { getFirebaseSingleton } from "@src/lib/firebase/src";
import { type ReactElement, useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { exchangeCognitoTokenForFirebase } from "../cognito/tokenExchange";
import { isLoginPath } from "./libs/utils/isLoginPath";

type AuthRedirectState = "ready" | "loading" | "success" | "error" | "email-input";

export function AuthRedirect(): ReactElement {
  const isNative = isCapacitorPlatform();
  const [state, setState] = useState<AuthRedirectState>(isNative ? "loading" : "ready");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [emailInput, setEmailInput] = useState<string>("");
  const history = useHistory();
  const location = useLocation();

  const fullUrl = globalThis.location.href;
  const searchParams = new URLSearchParams(location.search);
  const payload = searchParams.get("payload");
  const redirectUri = searchParams.get("redirectUri");

  const authenticateWithMagicLink = useCallback(
    async (email?: string): Promise<void> => {
      setState("loading");
      try {
        const cognito = getCognitoSingleton();

        // Resolve email for fresh-page scenario where there's no active session
        let resolvedEmail = email;
        if (!cognito.hasActiveSession && !resolvedEmail) {
          resolvedEmail = getMagicLinkEmail();
        }

        if (!cognito.hasActiveSession && !resolvedEmail) {
          setState("email-input");
          return;
        }

        // Sign in with Cognito using the magic link
        await cognito.signInWithEmailLink(fullUrl, resolvedEmail);

        // Exchange Cognito token for Firebase token (dual auth, same as OTP flow)
        const firebaseToken = await exchangeCognitoTokenForFirebase();
        await getFirebaseSingleton().signInWithCustomToken(firebaseToken);

        setState("success");

        const destination =
          redirectUri && !isLoginPath(redirectUri) ? redirectUri : RootPaths.APP_V2_HOME;

        logEvent(APP_V2_APP_EVENTS.COGNITO_MAGIC_LINK_CONFIRMED, {
          hasPayload: Boolean(payload),
          hasRedirectUri: Boolean(redirectUri),
        });

        history.replace(destination);
      } catch (error) {
        logError(APP_V2_APP_EVENTS.COGNITO_MAGIC_LINK_CONFIRMED, {
          error,
          metadata: { stage: "auth_redirect" },
        });

        const errorWithCode = error as { code?: string; message?: string };
        const message =
          errorWithCode.message ?? "Authentication failed. Please try requesting a new magic link.";
        setErrorMessage(message);
        setState("error");
      }
    },
    [fullUrl, history, payload, redirectUri]
  );

  // On native platforms, authenticate automatically
  useEffect(() => {
    if (!payload) {
      setErrorMessage("Invalid magic link. Please request a new one.");
      setState("error");
      return;
    }

    if (isNative) {
      void authenticateWithMagicLink();
    }
  }, [isNative, payload, authenticateWithMagicLink]);

  const handleBackToLogin = useCallback((): void => {
    history.replace(RootPaths.WELCOME);
  }, [history]);

  return (
    <ThemeProvider theme={getTheme()}>
      <AnimatedBackgroundPageWrapper>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minHeight: "100%",
            paddingY: 4,
            paddingX: 6,
            position: "relative",
            zIndex: 3,
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Container maxWidth="sm">
              <Stack spacing={4} alignItems="center" textAlign="center">
                <ClipboardLogo width={200} />

                {state === "ready" && (
                  <>
                    <Text variant="h5" sx={{ color: (theme) => theme.palette.primary.light }}>
                      You received a magic link to sign in.
                      <br />
                      Click the button below to complete authentication.
                    </Text>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => {
                        void authenticateWithMagicLink();
                      }}
                    >
                      Sign In
                    </Button>
                  </>
                )}

                {state === "loading" && (
                  <>
                    <CircularProgress sx={{ color: "white" }} />
                    <Text variant="h5" sx={{ color: (theme) => theme.palette.primary.light }}>
                      Authenticating...
                    </Text>
                  </>
                )}

                {state === "success" && (
                  <Text variant="h5" sx={{ color: (theme) => theme.palette.primary.light }}>
                    Success! Redirecting...
                  </Text>
                )}

                {state === "email-input" && (
                  <>
                    <Text variant="h5" sx={{ color: (theme) => theme.palette.primary.light }}>
                      Enter the email you used to request the magic link.
                    </Text>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={emailInput}
                      sx={{
                        "& .MuiInputBase-root": {
                          backgroundColor: "white",
                        },
                      }}
                      onChange={(event) => {
                        setEmailInput(event.target.value);
                      }}
                    />
                    <Button
                      variant="contained"
                      size="large"
                      disabled={!emailInput.trim()}
                      onClick={() => {
                        void authenticateWithMagicLink(emailInput.trim());
                      }}
                    >
                      Continue
                    </Button>
                  </>
                )}

                {state === "error" && (
                  <>
                    <Text variant="h5" sx={{ color: (theme) => theme.palette.error.light }}>
                      Authentication Failed
                    </Text>
                    <Text sx={{ color: (theme) => theme.palette.primary.light }}>
                      {errorMessage}
                    </Text>
                    <Button sx={{ marginTop: 2 }} variant="contained" onClick={handleBackToLogin}>
                      Back to Login
                    </Button>
                  </>
                )}
              </Stack>
            </Container>
          </Box>
        </Box>
      </AnimatedBackgroundPageWrapper>
    </ThemeProvider>
  );
}
